import {
  useId,
  useMemo,
  useState,
  type HTMLAttributes,
  type KeyboardEvent,
} from "react";

import { Text } from "./Typography";
import { markerSpacing } from "./tokens";

export type TraceWaterfallVariant = "waterfall" | "flame";

// The API adapter from typed TraceNode responses lives at the app edge
// (`web/src/lib/traces.ts`); this UI primitive stays API-agnostic.
export interface TraceWaterfallSpan {
  children?: TraceWaterfallSpan[];
  endMs: number;
  id: string;
  name: string;
  startMs: number;
}

export interface TraceWaterfallProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "children"
> {
  activeSpanId?: string;
  ariaLabel?: string;
  defaultExpandedSpanIds?: string[];
  defaultSelectedSpanId?: string;
  expandedSpanIds?: string[];
  onExpandedSpanIdsChange?: (spanIds: string[]) => void;
  onSpanSelect?: (span: TraceWaterfallSpan) => void;
  selectedSpanId?: string;
  spans: TraceWaterfallSpan[];
  variant?: TraceWaterfallVariant;
}

interface TraceWaterfallFlatSpan {
  depth: number;
  expanded: boolean;
  hasChildren: boolean;
  span: TraceWaterfallSpan;
}

interface TraceWaterfallBounds {
  endMs: number;
  startMs: number;
}

const waterfallRowHeight = Number.parseInt(markerSpacing[8], 10);
const waterfallBarHeight = Number.parseInt(markerSpacing[4], 10);
const flameRowHeight = Number.parseInt(markerSpacing[9], 10);
const flameBarHeight = Number.parseInt(markerSpacing[6], 10);

function spanDuration(span: TraceWaterfallSpan): number {
  return Math.max(0, span.endMs - span.startMs);
}

function formatMilliseconds(milliseconds: number): string {
  if (!Number.isFinite(milliseconds)) {
    return "--";
  }

  if (milliseconds < 1000) {
    return `${Math.round(milliseconds).toString()}ms`;
  }

  if (milliseconds < 10_000) {
    return `${(milliseconds / 1000).toFixed(2)}s`;
  }

  return `${(milliseconds / 1000).toFixed(1)}s`;
}

function allSpans(spans: TraceWaterfallSpan[]): TraceWaterfallSpan[] {
  return spans.flatMap((span) => [span, ...allSpans(span.children ?? [])]);
}

function expandableSpanIds(spans: TraceWaterfallSpan[]): string[] {
  return spans.flatMap((span) => {
    const childIds = expandableSpanIds(span.children ?? []);
    return span.children?.length ? [span.id, ...childIds] : childIds;
  });
}

function traceBounds(spans: TraceWaterfallSpan[]): TraceWaterfallBounds {
  const flattened = allSpans(spans);
  if (flattened.length === 0) {
    return { endMs: 1, startMs: 0 };
  }

  const startMs = Math.min(...flattened.map((span) => span.startMs));
  const endMs = Math.max(...flattened.map((span) => span.endMs));
  if (
    !Number.isFinite(startMs) ||
    !Number.isFinite(endMs) ||
    endMs <= startMs
  ) {
    return { endMs: startMs + 1, startMs };
  }

  return { endMs, startMs };
}

function flattenVisibleSpans(
  spans: TraceWaterfallSpan[],
  expandedSpanIds: Set<string>,
  depth = 0,
): TraceWaterfallFlatSpan[] {
  return spans.flatMap((span) => {
    const hasChildren = Boolean(span.children?.length);
    const expanded = hasChildren && expandedSpanIds.has(span.id);
    const current = [{ depth, expanded, hasChildren, span }];
    if (!expanded) {
      return current;
    }

    return [
      ...current,
      ...flattenVisibleSpans(span.children ?? [], expandedSpanIds, depth + 1),
    ];
  });
}

function clampPercent(value: number): number {
  return Math.min(100, Math.max(0, value));
}

function spanX(span: TraceWaterfallSpan, bounds: TraceWaterfallBounds): number {
  const totalDuration = bounds.endMs - bounds.startMs;
  return clampPercent(((span.startMs - bounds.startMs) / totalDuration) * 100);
}

function spanWidth(
  span: TraceWaterfallSpan,
  bounds: TraceWaterfallBounds,
): number {
  const totalDuration = bounds.endMs - bounds.startMs;
  const width = (spanDuration(span) / totalDuration) * 100;
  return clampPercent(width);
}

function visualSpanGeometry(
  span: TraceWaterfallSpan,
  bounds: TraceWaterfallBounds,
): { width: number; x: number } {
  const rawX = spanX(span, bounds);
  const rawWidth = spanWidth(span, bounds);
  const width = Math.min(Math.max(rawWidth, 0.6), 100);
  return {
    width,
    x: Math.min(rawX, 100 - width),
  };
}

function toggleId(current: Set<string>, id: string): string[] {
  const next = new Set(current);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  return [...next];
}

function isKeyboardActivation(event: KeyboardEvent): boolean {
  return event.key === "Enter" || event.key === " ";
}

export function TraceWaterfall({
  activeSpanId,
  ariaLabel,
  className,
  defaultExpandedSpanIds,
  defaultSelectedSpanId,
  expandedSpanIds,
  onExpandedSpanIdsChange,
  onSpanSelect,
  selectedSpanId,
  spans,
  variant = "waterfall",
  ...props
}: TraceWaterfallProps) {
  const tooltipId = useId();
  const [internalExpandedSpanIds, setInternalExpandedSpanIds] = useState<
    string[]
  >(() => defaultExpandedSpanIds ?? expandableSpanIds(spans));
  const [internalSelectedSpanId, setInternalSelectedSpanId] = useState<
    string | undefined
  >(defaultSelectedSpanId);
  const [tooltipSpanId, setTooltipSpanId] = useState<string | undefined>();

  const resolvedExpandedIds = expandedSpanIds ?? internalExpandedSpanIds;
  const expandedSet = useMemo(
    () => new Set(resolvedExpandedIds),
    [resolvedExpandedIds],
  );
  const flattenedSpans = useMemo(
    () => flattenVisibleSpans(spans, expandedSet),
    [expandedSet, spans],
  );
  const bounds = useMemo(() => traceBounds(spans), [spans]);
  const resolvedSelectedSpanId = selectedSpanId ?? internalSelectedSpanId;
  const tooltipSpan = flattenedSpans.find(
    (item) => item.span.id === tooltipSpanId,
  )?.span;
  const classes = ["marker-trace-waterfall"];
  classes.push(`marker-trace-waterfall--${variant}`);
  if (className) {
    classes.push(className);
  }

  function updateExpandedSpanIds(nextSpanIds: string[]) {
    if (expandedSpanIds === undefined) {
      setInternalExpandedSpanIds(nextSpanIds);
    }
    onExpandedSpanIdsChange?.(nextSpanIds);
  }

  function handleToggle(span: TraceWaterfallSpan) {
    updateExpandedSpanIds(toggleId(expandedSet, span.id));
  }

  function handleSelect(span: TraceWaterfallSpan) {
    if (selectedSpanId === undefined) {
      setInternalSelectedSpanId(span.id);
    }
    onSpanSelect?.(span);
  }

  function activeDescriptionId(span: TraceWaterfallSpan): string | undefined {
    return tooltipSpanId === span.id ? tooltipId : undefined;
  }

  function renderWaterfallRows() {
    return (
      <div className="marker-trace-waterfall__rows" role="tree">
        {flattenedSpans.map(({ depth, expanded, hasChildren, span }) => {
          const selected = resolvedSelectedSpanId === span.id;
          const active = activeSpanId === span.id;
          const rowClasses = ["marker-trace-waterfall__row"];
          if (selected || active) {
            rowClasses.push("marker-trace-waterfall__row--active");
          }
          const geometry = visualSpanGeometry(span, bounds);

          return (
            <div
              aria-expanded={hasChildren ? expanded : undefined}
              aria-level={depth + 1}
              aria-selected={selected}
              className={rowClasses.join(" ")}
              key={span.id}
              role="treeitem"
            >
              <div className="marker-trace-waterfall__label">
                {Array.from({ length: depth }, (_, index) => (
                  <span
                    aria-hidden="true"
                    className="marker-trace-waterfall__depth-guide"
                    key={`${span.id}-depth-${index.toString()}`}
                  />
                ))}
                {hasChildren ? (
                  <button
                    aria-label={`${expanded ? "Collapse" : "Expand"} span ${
                      span.name
                    }`}
                    className="marker-trace-waterfall__toggle"
                    onClick={() => {
                      handleToggle(span);
                    }}
                    type="button"
                  >
                    {expanded ? "-" : "+"}
                  </button>
                ) : (
                  <span
                    aria-hidden="true"
                    className="marker-trace-waterfall__toggle-spacer"
                  />
                )}
                <button
                  aria-label={`Select span ${span.name}`}
                  className="marker-trace-waterfall__name"
                  onClick={() => {
                    handleSelect(span);
                  }}
                  type="button"
                >
                  {span.name}
                </button>
              </div>
              <button
                aria-describedby={activeDescriptionId(span)}
                aria-label={`Select timeline span ${span.name}, ${formatMilliseconds(
                  spanDuration(span),
                )}`}
                aria-pressed={selected}
                className="marker-trace-waterfall__bar-button"
                onBlur={() => {
                  setTooltipSpanId(undefined);
                }}
                onClick={() => {
                  handleSelect(span);
                }}
                onFocus={() => {
                  setTooltipSpanId(span.id);
                }}
                onMouseEnter={() => {
                  setTooltipSpanId(span.id);
                }}
                onMouseLeave={() => {
                  setTooltipSpanId(undefined);
                }}
                type="button"
              >
                <svg
                  aria-hidden="true"
                  className="marker-trace-waterfall__bar-svg"
                  focusable="false"
                  preserveAspectRatio="none"
                  viewBox={`0 0 100 ${waterfallRowHeight.toString()}`}
                >
                  <rect
                    className="marker-trace-waterfall__bar"
                    height={waterfallBarHeight}
                    width={geometry.width}
                    x={geometry.x}
                    y={(waterfallRowHeight - waterfallBarHeight) / 2}
                  />
                </svg>
              </button>
              <Text
                as="span"
                className="marker-trace-waterfall__duration"
                variant="small"
              >
                {formatMilliseconds(spanDuration(span))}
              </Text>
            </div>
          );
        })}
      </div>
    );
  }

  function renderFlameGraph() {
    const maxDepth = Math.max(0, ...flattenedSpans.map((item) => item.depth));
    const height = (maxDepth + 1) * flameRowHeight;

    return (
      <div className="marker-trace-waterfall__flame">
        <svg
          aria-label={ariaLabel ?? "Trace flame graph"}
          className="marker-trace-waterfall__flame-svg"
          preserveAspectRatio="none"
          role="group"
          viewBox={`0 0 100 ${height.toString()}`}
        >
          {flattenedSpans.map(({ depth, expanded, hasChildren, span }) => {
            const selected = resolvedSelectedSpanId === span.id;
            const active = activeSpanId === span.id;
            const geometry = visualSpanGeometry(span, bounds);
            const segmentClasses = ["marker-trace-waterfall__flame-segment"];
            if (selected || active) {
              segmentClasses.push(
                "marker-trace-waterfall__flame-segment--active",
              );
            }

            return (
              <g
                aria-describedby={activeDescriptionId(span)}
                aria-expanded={hasChildren ? expanded : undefined}
                aria-label={`Select flame span ${
                  span.name
                }, ${formatMilliseconds(spanDuration(span))}`}
                aria-pressed={selected}
                className={segmentClasses.join(" ")}
                key={span.id}
                onBlur={() => {
                  setTooltipSpanId(undefined);
                }}
                onClick={() => {
                  handleSelect(span);
                  if (hasChildren) {
                    handleToggle(span);
                  }
                }}
                onFocus={() => {
                  setTooltipSpanId(span.id);
                }}
                onKeyDown={(event) => {
                  if (isKeyboardActivation(event)) {
                    event.preventDefault();
                    handleSelect(span);
                    if (hasChildren) {
                      handleToggle(span);
                    }
                  }
                }}
                onMouseEnter={() => {
                  setTooltipSpanId(span.id);
                }}
                onMouseLeave={() => {
                  setTooltipSpanId(undefined);
                }}
                role="button"
                tabIndex={0}
              >
                <rect
                  height={flameBarHeight}
                  width={geometry.width}
                  x={geometry.x}
                  y={depth * flameRowHeight + (flameRowHeight - flameBarHeight)}
                />
              </g>
            );
          })}
        </svg>
        <div className="marker-trace-waterfall__flame-legend" role="tree">
          {flattenedSpans.map(({ depth, expanded, hasChildren, span }) => (
            <div
              aria-expanded={hasChildren ? expanded : undefined}
              aria-level={depth + 1}
              className="marker-trace-waterfall__flame-legend-row"
              key={`${span.id}-legend`}
              role="treeitem"
            >
              <div className="marker-trace-waterfall__label">
                {Array.from({ length: depth }, (_, index) => (
                  <span
                    aria-hidden="true"
                    className="marker-trace-waterfall__depth-guide"
                    key={`${span.id}-flame-depth-${index.toString()}`}
                  />
                ))}
                <button
                  aria-label={
                    hasChildren
                      ? `${expanded ? "Collapse" : "Expand"} span ${span.name}`
                      : `Select span ${span.name}`
                  }
                  className="marker-trace-waterfall__name"
                  onClick={() => {
                    if (hasChildren) {
                      handleToggle(span);
                    } else {
                      handleSelect(span);
                    }
                  }}
                  type="button"
                >
                  {span.name}
                </button>
              </div>
              <Text
                as="span"
                className="marker-trace-waterfall__duration"
                variant="small"
              >
                {formatMilliseconds(spanDuration(span))}
              </Text>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      aria-label={ariaLabel ?? "Trace waterfall"}
      className={classes.join(" ")}
      role="region"
      {...props}
    >
      <div className="marker-trace-waterfall__header">
        <Text as="span" variant="caps">
          Span
        </Text>
        <Text as="span" variant="caps">
          {variant === "flame" ? "Flame" : "Timeline"}
        </Text>
        <Text as="span" variant="caps">
          Duration
        </Text>
      </div>
      {flattenedSpans.length > 0 ? (
        variant === "flame" ? (
          renderFlameGraph()
        ) : (
          renderWaterfallRows()
        )
      ) : (
        <div className="marker-trace-waterfall__empty">No spans.</div>
      )}
      {tooltipSpan ? (
        <div
          className="marker-trace-waterfall__tooltip"
          id={tooltipId}
          role="tooltip"
        >
          <Text as="span" variant="small">
            {tooltipSpan.name}
          </Text>
          <Text as="span" variant="small">
            {formatMilliseconds(tooltipSpan.startMs - bounds.startMs)} start
            {" / "}
            {formatMilliseconds(spanDuration(tooltipSpan))} duration
          </Text>
        </div>
      ) : null}
    </div>
  );
}
